import moment from 'moment';

import {
  AVAILABILITY_LABELS,
  ADMIN_COPY,
  CSV_HEADERS,
  DATE_TIME_LOCAL_FORMAT,
  EVENT_AVAILABILITY,
  EVENT_COPY,
  EVENT_DATE_FORMAT,
  EVENT_ERROR_MESSAGES,
  EVENT_LIMITS,
  EVENT_STATUSES,
  EVENT_TIME_FORMAT,
  GUEST_RSVP_STORAGE_PREFIX,
  RSVP_STATUS_LABELS,
  RSVP_STATUSES,
  STAFF_NAME_STORAGE_KEY,
} from './constants';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PHONE_DIGITS = 7;
const MAX_PHONE_DIGITS = 15;
const CSV_FORMULA_PREFIX = /^[=+\-@\t\r]/;

const safeStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (error) {
    return null;
  }
};

export const formatEventDate = (ms) =>
  Number.isFinite(Number(ms)) ? moment(Number(ms)).format(EVENT_DATE_FORMAT) : '';

export const formatEventTime = (ms) =>
  Number.isFinite(Number(ms)) ? moment(Number(ms)).format(EVENT_TIME_FORMAT) : '';

export const formatEventWhen = (startsAtMs, endsAtMs) => {
  if (!Number.isFinite(Number(startsAtMs))) {
    return '';
  }
  const start = moment(Number(startsAtMs));
  const base = `${start.format(EVENT_DATE_FORMAT)}, ${start.format(EVENT_TIME_FORMAT)}`;
  if (!Number.isFinite(Number(endsAtMs)) || !endsAtMs) {
    return base;
  }
  const end = moment(Number(endsAtMs));
  if (end.isSame(start, 'day')) {
    return `${base} - ${end.format(EVENT_TIME_FORMAT)}`;
  }
  return `${base} - ${end.format(EVENT_DATE_FORMAT)}, ${end.format(EVENT_TIME_FORMAT)}`;
};

export const hasCapacity = (event) =>
  event?.capacity !== null && event?.capacity !== undefined && Number(event.capacity) > 0;

export const getSpotsLeft = (event) => {
  if (!hasCapacity(event)) {
    return null;
  }
  return Math.max(0, Number(event.capacity) - (Number(event.goingCount) || 0));
};

export const formatCapacitySummary = (event) => {
  const going = Number(event?.goingCount) || 0;
  const waitlisted = Number(event?.waitlistedCount) || 0;
  const parts = [];
  if (hasCapacity(event)) {
    const left = getSpotsLeft(event);
    parts.push(`${going} / ${event.capacity} going`);
    parts.push(left === 0 ? 'No spots left' : `${left} ${left === 1 ? 'spot' : 'spots'} left`);
  } else {
    parts.push(`${going} going`);
    parts.push('Unlimited spots');
  }
  if (waitlisted > 0) {
    parts.push(`${waitlisted} on waitlist`);
  }
  return parts.join(' - ');
};

export const getEventAvailability = (event, nowMs = Date.now()) => {
  if (event?.status === EVENT_STATUSES.CANCELLED) {
    return EVENT_AVAILABILITY.CANCELLED;
  }
  if (event?.status === EVENT_STATUSES.CLOSED) {
    return EVENT_AVAILABILITY.CLOSED;
  }
  if (Number(event?.startsAtMs) <= nowMs) {
    return EVENT_AVAILABILITY.STARTED;
  }
  if (hasCapacity(event) && getSpotsLeft(event) === 0) {
    return event?.allowWaitlist === false
      ? EVENT_AVAILABILITY.FULL
      : EVENT_AVAILABILITY.FULL_WAITLIST;
  }
  return EVENT_AVAILABILITY.OPEN;
};

export const getAvailabilityLabel = (availability) => AVAILABILITY_LABELS[availability] || '';

export const canChangeRsvp = (availability) =>
  availability === EVENT_AVAILABILITY.OPEN ||
  availability === EVENT_AVAILABILITY.FULL_WAITLIST ||
  availability === EVENT_AVAILABILITY.FULL;

export const getRsvpStatusLabel = (status) => RSVP_STATUS_LABELS[status] || status || '';

export const isActiveRsvp = (rsvp) => Boolean(rsvp) && rsvp.status !== RSVP_STATUSES.CANCELLED;

export const describeRsvp = (rsvp) => {
  if (!isActiveRsvp(rsvp)) {
    return '';
  }
  if (rsvp.status === RSVP_STATUSES.WAITLISTED) {
    const position = Number(rsvp.waitlistPosition);
    return Number.isFinite(position) && position > 0
      ? `You are on the waitlist (#${position}).`
      : 'You are on the waitlist.';
  }
  if (rsvp.status === RSVP_STATUSES.GOING) {
    return "You're going.";
  }
  if (rsvp.status === RSVP_STATUSES.MAYBE) {
    return "You're a maybe.";
  }
  return "You've said you can't make it.";
};

export const isSoftFailure = (response) => response?.data?.success === false;

export const extractErrorMessage = (errorOrResponse) => {
  const data = errorOrResponse?.data;
  const code = data?.errorCode;
  if (code && EVENT_ERROR_MESSAGES[code]) {
    return EVENT_ERROR_MESSAGES[code];
  }
  if (typeof data?.errorMessage === 'string' && data.errorMessage) {
    return data.errorMessage;
  }
  if (typeof data === 'string' && data && data !== 'Unauthorized') {
    return data;
  }
  if (typeof data?.message === 'string' && data.message) {
    return data.message;
  }
  if (typeof data?.error === 'string' && data.error) {
    return data.error;
  }
  return EVENT_COPY.GENERIC_ERROR;
};

export const getErrorCode = (errorOrResponse) => errorOrResponse?.data?.errorCode || '';

export const normalizePhoneDigits = (phone) => String(phone || '').replace(/\D/g, '');

export const validateGuestDetails = ({ name, email, phone } = {}) => {
  const errors = {};
  const trimmedName = String(name || '').trim();
  const trimmedEmail = String(email || '').trim();
  const trimmedPhone = String(phone || '').trim();

  if (!trimmedName) {
    errors.name = EVENT_COPY.GUEST_NAME_REQUIRED;
  }
  if (!trimmedEmail && !trimmedPhone) {
    errors.contact = EVENT_COPY.GUEST_CONTACT_REQUIRED;
  }
  if (trimmedEmail && !EMAIL_PATTERN.test(trimmedEmail)) {
    errors.email = EVENT_COPY.GUEST_EMAIL_INVALID;
  }
  if (trimmedPhone) {
    const digits = normalizePhoneDigits(trimmedPhone).length;
    if (digits < MIN_PHONE_DIGITS || digits > MAX_PHONE_DIGITS) {
      errors.phone = EVENT_COPY.GUEST_PHONE_INVALID;
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

export const getGuestStorageKey = (eventId) => `${GUEST_RSVP_STORAGE_PREFIX}${eventId}`;

export const loadGuestRsvp = (eventId) => {
  const storage = safeStorage();
  if (!storage || !eventId) {
    return null;
  }
  try {
    const parsed = JSON.parse(storage.getItem(getGuestStorageKey(eventId)));
    return parsed?.attendeeId && parsed?.cancelToken ? parsed : null;
  } catch (error) {
    return null;
  }
};

export const saveGuestRsvp = (eventId, value) => {
  const storage = safeStorage();
  if (!storage || !eventId) {
    return false;
  }
  try {
    storage.setItem(getGuestStorageKey(eventId), JSON.stringify(value));
    return true;
  } catch (error) {
    return false;
  }
};

export const clearGuestRsvp = (eventId) => {
  const storage = safeStorage();
  if (!storage || !eventId) {
    return;
  }
  try {
    storage.removeItem(getGuestStorageKey(eventId));
  } catch (error) {
    // storage unavailable
  }
};

export const loadStaffName = () => {
  const storage = safeStorage();
  try {
    return storage?.getItem(STAFF_NAME_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
};

export const saveStaffName = (name) => {
  const storage = safeStorage();
  try {
    storage?.setItem(STAFF_NAME_STORAGE_KEY, String(name || '').trim());
  } catch (error) {
    // storage unavailable
  }
};

export const getMemberUserId = (user) => user?.userId || null;

export const buildRsvpRequest = ({ eventId, status, user, guest, stored }) => {
  const userId = getMemberUserId(user);
  if (userId) {
    return { payload: { eventId, status, userId }, config: {} };
  }

  const payload = {
    eventId,
    status,
    name: String(guest?.name || '').trim(),
  };
  const email = String(guest?.email || '').trim();
  const phone = String(guest?.phone || '').trim();
  if (email) {
    payload.email = email;
  }
  if (phone) {
    payload.phone = phone;
  }
  if (stored?.attendeeId && stored?.cancelToken) {
    payload.attendeeId = stored.attendeeId;
    payload.cancelToken = stored.cancelToken;
  }
  return { payload, config: { skipAuth: true } };
};

export const buildIdentityRequest = ({ eventId, user, stored }) => {
  const userId = getMemberUserId(user);
  if (userId) {
    return { payload: { eventId, userId }, config: {} };
  }
  if (stored?.attendeeId && stored?.cancelToken) {
    return {
      payload: { eventId, attendeeId: stored.attendeeId, cancelToken: stored.cancelToken },
      config: { skipAuth: true },
    };
  }
  return null;
};

export const buildStoredGuest = ({ guest, response }) => {
  const data = response?.data;
  if (!data?.attendeeId || !data?.cancelToken) {
    return null;
  }
  return {
    attendeeId: data.attendeeId,
    cancelToken: data.cancelToken,
    name: String(guest?.name || '').trim(),
    email: String(guest?.email || '').trim(),
    phone: String(guest?.phone || '').trim(),
  };
};

export const toDateTimeLocalValue = (ms) =>
  Number.isFinite(Number(ms)) && ms ? moment(Number(ms)).format(DATE_TIME_LOCAL_FORMAT) : '';

export const fromDateTimeLocalValue = (value) => {
  if (!value) {
    return null;
  }
  const parsed = moment(value, DATE_TIME_LOCAL_FORMAT, true);
  return parsed.isValid() ? parsed.valueOf() : null;
};

export const buildEmptyEventForm = () => ({
  title: '',
  description: '',
  location: '',
  startsAt: '',
  endsAt: '',
  capacity: '',
  allowGuests: true,
  allowWaitlist: true,
  status: EVENT_STATUSES.OPEN,
});

export const eventToForm = (event) => ({
  title: event?.title || '',
  description: event?.description || '',
  location: event?.location || '',
  startsAt: toDateTimeLocalValue(event?.startsAtMs),
  endsAt: toDateTimeLocalValue(event?.endsAtMs),
  capacity: hasCapacity(event) ? String(event.capacity) : '',
  allowGuests: event?.allowGuests !== false,
  allowWaitlist: event?.allowWaitlist !== false,
  status: event?.status === EVENT_STATUSES.CLOSED ? EVENT_STATUSES.CLOSED : EVENT_STATUSES.OPEN,
});

export const validateEventForm = (form, { isEdit = false } = {}) => {
  const errors = {};
  const title = String(form?.title || '').trim();
  const description = String(form?.description || '').trim();
  const location = String(form?.location || '').trim();
  const startsAtMs = fromDateTimeLocalValue(form?.startsAt);
  const endsAtMs = fromDateTimeLocalValue(form?.endsAt);
  const capacityText = String(form?.capacity ?? '').trim();

  if (!title) {
    errors.title = ADMIN_COPY.TITLE_REQUIRED;
  } else if (title.length > EVENT_LIMITS.TITLE) {
    errors.title = ADMIN_COPY.TITLE_TOO_LONG;
  }
  if (description.length > EVENT_LIMITS.DESCRIPTION) {
    errors.description = ADMIN_COPY.DESCRIPTION_TOO_LONG;
  }
  if (location.length > EVENT_LIMITS.LOCATION) {
    errors.location = ADMIN_COPY.LOCATION_TOO_LONG;
  }
  if (!startsAtMs) {
    errors.startsAt = ADMIN_COPY.START_REQUIRED;
  }
  if (form?.endsAt && (!endsAtMs || (startsAtMs && endsAtMs <= startsAtMs))) {
    errors.endsAt = ADMIN_COPY.END_INVALID;
  }

  let capacity = null;
  if (capacityText) {
    const parsed = Number(capacityText);
    if (!Number.isInteger(parsed) || parsed < 1) {
      errors.capacity = ADMIN_COPY.CAPACITY_INVALID;
    } else {
      capacity = parsed;
    }
  }

  if (Object.keys(errors).length > 0) {
    return { isValid: false, errors, fields: null };
  }

  const fields = {
    title,
    description,
    location,
    startsAtMs,
    capacity,
    allowGuests: Boolean(form.allowGuests),
    allowWaitlist: Boolean(form.allowWaitlist),
  };
  if (endsAtMs) {
    fields.endsAtMs = endsAtMs;
  }
  if (isEdit) {
    fields.status =
      form.status === EVENT_STATUSES.CLOSED ? EVENT_STATUSES.CLOSED : EVENT_STATUSES.OPEN;
  }
  return { isValid: true, errors, fields };
};

export const sortEventsByStart = (events, direction = 'asc') => {
  const list = Array.isArray(events) ? [...events] : [];
  list.sort((a, b) =>
    direction === 'desc'
      ? Number(b?.startsAtMs) - Number(a?.startsAtMs)
      : Number(a?.startsAtMs) - Number(b?.startsAtMs)
  );
  return list;
};

export const getVisibleEvents = (events) =>
  (Array.isArray(events) ? events : []).filter(
    (event) => event?.status !== EVENT_STATUSES.CANCELLED
  );

export const mergeAttendees = (existing, incoming) => {
  const byId = new Map();
  [...(existing || []), ...(incoming || [])].forEach((attendee) => {
    if (attendee?.attendeeId) {
      byId.set(attendee.attendeeId, attendee);
    }
  });
  return Array.from(byId.values());
};

const ATTENDEE_STATUS_ORDER = Object.freeze({
  [RSVP_STATUSES.GOING]: 0,
  [RSVP_STATUSES.WAITLISTED]: 1,
  [RSVP_STATUSES.MAYBE]: 2,
  [RSVP_STATUSES.NOT_GOING]: 3,
  [RSVP_STATUSES.CANCELLED]: 4,
});

export const sortAttendees = (attendees) =>
  [...(attendees || [])].sort((a, b) => {
    const orderA = ATTENDEE_STATUS_ORDER[a?.status] ?? 99;
    const orderB = ATTENDEE_STATUS_ORDER[b?.status] ?? 99;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    if (a?.status === RSVP_STATUSES.WAITLISTED) {
      return (Number(a.waitlistPosition) || 0) - (Number(b.waitlistPosition) || 0);
    }
    return (Number(a?.createdAt) || 0) - (Number(b?.createdAt) || 0);
  });

export const summarizeAttendees = (attendees) => {
  const summary = { going: 0, maybe: 0, notGoing: 0, waitlisted: 0, cancelled: 0 };
  (attendees || []).forEach((attendee) => {
    if (attendee?.status === RSVP_STATUSES.GOING) summary.going += 1;
    else if (attendee?.status === RSVP_STATUSES.MAYBE) summary.maybe += 1;
    else if (attendee?.status === RSVP_STATUSES.NOT_GOING) summary.notGoing += 1;
    else if (attendee?.status === RSVP_STATUSES.WAITLISTED) summary.waitlisted += 1;
    else if (attendee?.status === RSVP_STATUSES.CANCELLED) summary.cancelled += 1;
  });
  return summary;
};

export const escapeCsvCell = (value) => {
  let text = value === null || value === undefined ? '' : String(value);
  if (CSV_FORMULA_PREFIX.test(text)) {
    text = `'${text}`;
  }
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildAttendeesCsv = (attendees) => {
  const rows = sortAttendees(attendees).map((attendee) => [
    attendee.displayName,
    attendee.attendeeType,
    getRsvpStatusLabel(attendee.status),
    attendee.status === RSVP_STATUSES.WAITLISTED ? attendee.waitlistPosition : '',
    attendee.email,
    attendee.phone,
    attendee.createdAt ? new Date(Number(attendee.createdAt)).toISOString() : '',
  ]);
  return [CSV_HEADERS, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
};

export const buildCsvFileName = (event) => {
  const slug = String(event?.title || 'event')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'event'}-attendees.csv`;
};

export const downloadTextFile = (fileName, content, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
